// src/components/UserProfileScreen.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth, db } from '../firebase';  // Adjust path to your firebase.js
import { doc, getDoc } from 'firebase/firestore';
import './UserProfileScreen.css';

function UserProfileScreen() {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  
  // Profile state
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // On mount or when 'user' changes, fetch the user profile from Firestore
  useEffect(() => {
    async function fetchProfile() {
      if (!user) {
        // If not logged in, redirect to /login or show a message
        setLoading(false);
        return;
      }

      try {
        const docRef = doc(db, 'users', user.uid);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          // We found the user's profile doc
          setProfile(docSnap.data());
        } else {
          console.log('No such user profile in Firestore!');
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
      setLoading(false);
    }

    fetchProfile();
  }, [user]);

  const handleLogout = async () => {
    await auth.signOut();
    navigate('/login');
  };

  if (loading) {
    return <div className="profile-container">Loading profile...</div>;
  }

  if (!user) {
    return (
      <div className="profile-container">
        <p>User not logged in.</p>
      </div>
    );
  }

  // If profile is null, we can show a fallback or partial data
  const fullName = profile?.fullName || user.displayName || 'No Name';
  const username = profile?.username || user.email;
  const rating = profile?.rating || 0;
  const activityCount = profile?.activityCount || 0;
  const friends = profile?.friends || [];
  const submissions = profile?.submissions || [];

  return (
    <div className="profile-container">
      {/* Header with back, search, logout */}
      <header className="profile-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          &lt; Back
        </button>
        <div className="search-box">
          <input type="text" placeholder="Search..." />
        </div>
        <button className="logout-btn" onClick={handleLogout}>
          Log Out
        </button>
      </header>

      {/* Main Profile Content */}
      <div className="profile-content">
        <div className="profile-info">
          {/* Placeholder avatar or real one from profile.avatarUrl */}
          <img
            src={profile?.avatarUrl || '/user-placeholder.png'}
            alt="User Avatar"
            className="profile-avatar"
          />
          <h2 className="profile-name">{fullName}</h2>
          <p className="profile-username">@{username}</p>
          <p className="profile-rating">Rating: {rating}</p>
          <p className="profile-activity">Activity: {activityCount} posts</p>
        </div>

        <div className="profile-sections">
          {/* Friends Section */}
          <div className="friends-section">
            <h3>Friends</h3>
            <div className="friends-list">
              {friends.length > 0 ? (
                friends.map((friendName, idx) => (
                  <div key={idx} className="friend-item">
                    {friendName}
                  </div>
                ))
              ) : (
                <p>No friends yet.</p>
              )}
            </div>
          </div>

          {/* Submissions Section */}
          <div className="submissions-section">
            <h3>Submissions</h3>
            <div className="submissions-list">
              {submissions.length > 0 ? (
                submissions.map((sub, idx) => (
                  <div key={idx} className="submission-item">
                    {sub}
                  </div>
                ))
              ) : (
                <p>No submissions yet.</p>
              )}
            </div>
          </div>

          {/* Direct Messages Button */}
          <button className="dm-btn" onClick={() => navigate('/direct-messages')}>
            Go to Direct Messages
          </button>
        </div>
      </div>

      {/* Privacy Settings */}
      <div className="privacy-settings">
        <h3>Privacy Settings</h3>
        <p>
          Manage your privacy options and account settings. For example,
          controlling who can see your profile or contact you.
        </p>
      </div>
    </div>
  );
}

export default UserProfileScreen;
